import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { BiLogger } from '../../components/Hoc/BiLogger/BiLoggerProvider';
import { logGeneralError } from '../../utils/sentry';
import { validateMerchant } from '../../utils/serverAPI/validateMerchant';
import { safeStringify } from '../../utils/safeStringify';
import { WidgetMeta } from '../../types/WidgetProps';
import { AuthParams } from '../../types/AuthParams';
import { WidgetFedopsLogger } from '../../components/Hoc/Fedops/FedopsProvider';
import { InteractionName } from '../../types/InteractionName';
import { extractHostname } from '../../utils/url/extractHostname';
import {
  applePayMerchantValidationError,
  applePayMerchantValidationSucceeded,
  applePayStartMerchantValidation,
} from '../../utils/bi/events/applePay';

/**
 * Domain validation handler
 * @param data Object
 * @param auth
 * @param auth.appInstance Signed instance that is used as Authorization header in REST API calls
 * @param auth.revalidateAppInstance Mechanism to revalidate the expired signed instance
 */
export const validateMerchantHandler =
  (
    data: {
      meta: WidgetMeta;
      domain: string;
    },
    biLogger: BiLogger,
    auth: AuthParams,
    fedopsLogger: WidgetFedopsLogger,
  ) =>
  async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
    const { domain, meta } = data;
    const { appDefId, cartId, appInstanceId, siteId, visitorId } = meta;
    try {
      biLogger.report(
        applePayStartMerchantValidation({
          checkoutType: 'express',
          paymentProvider: PaymentProvider.WixPayUS,
          paymentmethod: PaymentMethod.ApplePay,
          cartId,
        }),
      );
      fedopsLogger.interactionStarted(InteractionName.ApplePayValidateMerchant);
      const sessionObj = await validateMerchant(
        {
          accountId: `${appDefId}:${appInstanceId}`,
          validationUrl: event.validationURL,
          domain: extractHostname(domain),
        },
        auth,
      );
      fedopsLogger.interactionEnded(InteractionName.ApplePayValidateMerchant);
      biLogger.report(
        applePayMerchantValidationSucceeded({
          cartId,
          msid: siteId,
          paymentmethod: PaymentMethod.ApplePay,
          paymentProvider: PaymentProvider.WixPayUS,
          checkoutType: 'express',
          visitorId,
        }),
      );
      return sessionObj;
    } catch (error) {
      const sentryId = logGeneralError({
        message: '[ApplePay] validateMerchant failed',
        payload: error,
      });
      biLogger.report(
        applePayMerchantValidationError({
          checkoutType: 'express',
          errorReason: safeStringify({
            message: error.response?.data?.message || error.message,
            status: error.response?.status,
            requestId: error.requestId,
            sentryId,
          }),
          paymentProvider: PaymentProvider.WixPayUS,
          paymentmethod: PaymentMethod.ApplePay,
          cartId,
        }),
      );

      return error;
    }
  };
